/**
 * CredibilityLayer.js
 * Institutional transparency and disclosure components for Oko-Oba project.
 *
 * Components:
 *   CredibilityStatusBanner   — shows Calculated/Reviewed/Verified status prominently
 *   MethodologyPanel          — methodology disclosure for climate, circularity, finance
 *   ConfidenceBreakdownPanel  — detailed confidence breakdown (not just a score)
 *   FinancialAssumptionsPanel — key financial assumptions displayed explicitly
 *   CarbonReadinessPanel      — carbon crediting readiness assessment
 *   OutputClassificationTag   — "(Calculated Estimate)" inline label for key metrics
 *
 * Rules:
 *   - No calculations performed here — only display/disclosure
 *   - Reads from projectState, dataQualityOutputs, journey (structured outputs)
 *   - Non-blocking — never prevents user actions
 *   - Must appear in UI and exported reports
 */
import React, { useState } from "react";
import { Shield, AlertTriangle, Info, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, AlertCircle, BookOpen, DollarSign, Leaf } from "lucide-react";

// ── Status label mapping ───────────────────────────────────────────────────────
const STATUS_MAP = {
  preliminary:          { label: "Pre-Feasibility — Model-Based Estimates Only",      color: "bg-amber-50  border-amber-300  text-amber-800",  dot: "bg-amber-500",  badge: "Pre-Feasibility" },
  uploaded:             { label: "Data Submitted — Pending Internal Review",           color: "bg-blue-50   border-blue-300   text-blue-800",   dot: "bg-blue-500",   badge: "Submitted" },
  reviewed_internal:    { label: "Calculated (Internally Reviewed — Not Third-Party Validated)", color: "bg-teal-50 border-teal-300 text-teal-800", dot: "bg-teal-500", badge: "Internally Reviewed" },
  verified_independent: { label: "Independent Verification Not Initiated", color: "bg-teal-50 border-teal-300 text-teal-800", dot: "bg-teal-500", badge: "Not Initiated" },
  certified:            { label: "Certified — Eligible for Market Use",                color: "bg-emerald-100 border-emerald-400 text-emerald-900", dot: "bg-emerald-600", badge: "Certified" },
};

// ── 1. Credibility Status Banner ──────────────────────────────────────────────
export function CredibilityStatusBanner({ projectState, compact = false }) {
  const rep     = (projectState?.report) || {};
  const verLevel= rep.verification_level || "preliminary";
  const cfg     = STATUS_MAP[verLevel] || STATUS_MAP.preliminary;
  const isCalc  = ["preliminary","uploaded","reviewed_internal"].includes(verLevel);

  if (compact) return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${cfg.color}`}
      data-testid="credibility-status-compact">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
      <span className="text-xs font-bold">{cfg.badge}</span>
    </div>
  );

  return (
    <div className={`border-2 rounded-xl px-5 py-3.5 flex items-start gap-3 ${cfg.color}`}
      data-testid="credibility-status-banner">
      <Shield size={16} className="flex-shrink-0 mt-0.5 opacity-80" />
      <div className="flex-1">
        <p className="text-sm font-bold">{cfg.label}</p>
        {isCalc && (
          <p className="text-xs opacity-75 mt-0.5">
            Outputs are model-based estimates generated from user inputs and defined assumptions.
            They have not undergone independent third-party verification or certification.
            {verLevel === "reviewed_internal" && " Internal platform review is complete but does not substitute for accredited VVB validation."}
          </p>
        )}
        {verLevel === "verified_independent" && (
          <p className="text-xs opacity-75 mt-0.5">
            Platform verification is complete. For carbon market eligibility or DFI formal submission,
            independent VVB (Validation/Verification Body) engagement is required.
          </p>
        )}
      </div>
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full bg-white/40 flex-shrink-0`}>
        {cfg.badge}
      </span>
    </div>
  );
}

// ── 2. Methodology Disclosure Panel ──────────────────────────────────────────
export function MethodologyPanel({ methodology, calculationBasis, protocol, creditingPathway, sector, compact = false }) {
  const [open, setOpen] = useState(!compact);

  // Resolve 3-layer values — backward compatible: fall back to legacy `methodology` for protocol
  const calcBasis   = calculationBasis || "IPCC 2006 Guidelines";
  const protocolId  = protocol || methodology || "DCI-BIOGAS-V1.0";
  const creditPw    = creditingPathway !== undefined && creditingPathway !== null
    ? creditingPathway
    : "Not Assigned";

  const METHODOLOGIES = {
    "climate": {
      label: "Climate Methodology",
      ref:   protocolId,
      basis: calcBasis,
      detail: "GHG emissions reduction calculated using First-Order Decay (FOD) model for methane generation. Emission factors from IPCC 2006 Vol.5 Table 2.4 (DOC values) and Table 3.2 (MCF values). Energy displacement emission factors from IEA World Energy Balances 2023. GWP per selected IPCC Assessment Report (AR4/AR6).",
      standard: "Consistent with GHG Protocol Corporate Value Chain Standard and IFRS S2 pre-screening guidance.",
    },
    "circularity": {
      label: "Circularity Methodology",
      ref:   "DeCarbonIQ Circularity Framework",
      basis: "DeCarbonIQ Circularity Framework (aligned with global circular economy principles, including Ellen MacArthur Foundation guidance)",
      detail: "Circularity score = weighted(waste_diversion 35% + energy_recovery 30% + resource_recovery 20% + loop_completeness 15%). Scored 0–100 with thresholds: LOW <40, MEDIUM 40–70, HIGH ≥70. All inputs derived from verified engine outputs — no independent recomputation.",
      standard: "Internal DeCarbonIQ framework. Not currently aligned with an externally audited circularity standard.",
    },
    "finance": {
      label: "Financial Model",
      ref:   "Project Finance Model (DCI)",
      basis: "Standard project finance methodology: revenue, DSCR, IRR, payback",
      detail: "Financial model based on user-provided CAPEX, OPEX, electricity tariff, tipping fee, and debt terms. DSCR computed as annual net operating cashflow / annual debt service. IRR computed as internal rate of return over project life. All projections are indicative and subject to independent technical and financial due diligence.",
      standard: "Not an investment opinion. All figures are indicative only.",
    },
    "carbon": {
      label: "Carbon Pathway Assessment",
      ref:   "CDE Pre-Screening (DCI)",
      basis: "UNFCCC CDM / Verra VCS / Article 6 eligibility pre-screening",
      detail: "Carbon pathway eligibility assessed by the Central Decision Engine (CDE) against defined prerequisites: scale threshold, baseline confirmation, data completeness, and evidence coverage. This is a pre-screening assessment only — it does not constitute formal determination of eligibility under any carbon standard.",
      standard: "Pre-screening only. Formal eligibility requires VVB review, project registration, and methodology application.",
    },
  };

  const items = Object.values(METHODOLOGIES);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden"
      data-testid="methodology-panel">
      <button className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 text-left"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2 flex-wrap">
          <BookOpen size={13} className="text-teal-600 flex-shrink-0" />
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Methodology Disclosure</p>
          <span className="text-xs text-slate-400 normal-case font-normal">
            · Calculation Basis ({calcBasis.split(" ").slice(0,3).join(" ")}) · Protocol ({protocolId}) · Crediting Pathway ({creditPw})
          </span>
        </div>
        {open ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0" /> : <ChevronDown size={13} className="text-slate-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="divide-y divide-slate-100">
          {/* 3-layer architecture summary */}
          <div className="px-5 py-3 bg-slate-50/60">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Methodology Architecture</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1.5">
              <div>
                <span className="text-xs text-slate-400">Calculation Basis:</span>
                <span className="text-xs font-semibold text-slate-700 ml-1">{calcBasis}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400">Protocol:</span>
                <span className="text-xs font-semibold text-slate-700 ml-1 font-mono">{protocolId}</span>
              </div>
              <div>
                <span className="text-xs text-slate-400">Crediting Pathway:</span>
                <span className={`text-xs font-semibold ml-1 ${creditPw === "Not Assigned" ? "text-amber-600" : "text-teal-700"}`}>{creditPw}</span>
              </div>
            </div>
          </div>
          {items.map(m => (
            <div key={m.label} className="px-5 py-3">
              <div className="flex items-start justify-between mb-1">
                <p className="text-xs font-bold text-slate-700">{m.label}</p>
                <span className="text-xs text-teal-600 font-mono ml-2 flex-shrink-0">{m.ref}</span>
              </div>
              <p className="text-xs text-slate-500 mb-1 font-semibold">{m.basis}</p>
              <p className="text-xs text-slate-400 leading-relaxed">{m.detail}</p>
              <p className="text-xs text-amber-600 mt-1 italic">{m.standard}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 3. Confidence Breakdown Panel ────────────────────────────────────────────
export function ConfidenceBreakdownPanel({ dataQualityOutputs, dataConfidence }) {
  const dq = dataQualityOutputs || {};
  const dc = dataConfidence || {};
  const bd = dc.confidence_breakdown || {};

  const score     = dq.confidence_score || dc.data_confidence_score || 0;
  const level     = dq.confidence_level || dc.label || "Low";
  const evPct     = dq.evidence_coverage_pct || 0;
  const tierMul   = bd.tier_multiplier || 0.75;
  const estPct    = bd.estimated_pct || 100;
  const audReady  = dq.audit_ready || false;
  const verStatus = dq.can_publish ? "Meets platform threshold" : "Not yet verified";
  const verLevel  = dc.verification_level || "preliminary";
  const anomalies = dc.anomaly_flags || [];

  // Derived labels
  const tierQuality = tierMul >= 0.9 ? "High (T1 dominant)" : tierMul >= 0.75 ? "Medium (mixed T1/T2)" : "Low (T3 dominant)";
  const assumptionDep = bd.assumptions >= 20 ? "Low" : bd.assumptions >= 12 ? "Medium" : "High";
  const evCompl = evPct >= 80 ? "High" : evPct >= 50 ? "Medium" : "Low";

  const levelColor = { High: "text-emerald-700 bg-emerald-50", Medium: "text-amber-700 bg-amber-50", Low: "text-red-600 bg-red-50" }[level] || "text-slate-600 bg-slate-50";

  const ROWS = [
    ["Data Coverage",         `${evPct}%`,           evPct >= 80 ? "emerald" : evPct >= 50 ? "amber" : "red"],
    ["Data Tier Quality",     tierQuality,            tierMul >= 0.9 ? "emerald" : tierMul >= 0.75 ? "amber" : "red"],
    ["Assumption Dependency", assumptionDep,          assumptionDep === "Low" ? "emerald" : assumptionDep === "Medium" ? "amber" : "red"],
    ["Evidence Completeness", evCompl,                evPct >= 70 ? "emerald" : evPct >= 40 ? "amber" : "red"],
    ["Data Quality Status",   audReady ? "High (Model completeness achieved)" : "Partial (validation pending)", audReady ? "emerald" : "amber"],
    ["Verification Status",   STATUS_MAP[verLevel]?.badge || "Not verified", verLevel === "verified_independent" ? "emerald" : verLevel === "reviewed_internal" ? "teal" : "amber"],
    ["Anomaly Flags",         anomalies.length === 0 ? "None detected" : `${anomalies.length} flag(s)`, anomalies.length === 0 ? "emerald" : "amber"],
  ];

  const dotColor = { emerald: "bg-emerald-500", teal: "bg-teal-500", amber: "bg-amber-400", red: "bg-red-500" };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="confidence-breakdown">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Confidence Breakdown</p>
        </div>
        <div className={`text-sm font-bold px-3 py-1 rounded-full ${levelColor}`}>
          {score}/100 — {level}
        </div>
      </div>
      <div className="space-y-2">
        {ROWS.map(([label, value, color]) => (
          <div key={label} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
            <p className="text-xs text-slate-600">{label}</p>
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor[color] || "bg-slate-300"}`} />
              <span className="text-xs font-semibold text-slate-800">{value}</span>
            </div>
          </div>
        ))}
      </div>
      {dc.note && (
        <p className="text-xs text-slate-400 mt-3 pt-3 border-t border-slate-100 italic">{dc.note}</p>
      )}
      {anomalies.length > 0 && (
        <div className="mt-3 space-y-1">
          {anomalies.map((a, i) => (
            <div key={i} className="flex items-start gap-1.5 text-xs text-amber-700">
              <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
              <span>{a}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 4. Financial Assumptions Panel ────────────────────────────────────────────
export function FinancialAssumptionsPanel({ journey, financialOutputs }) {
  const [open, setOpen] = useState(false);
  const inp  = journey?.input_snapshot || {};
  const fo   = financialOutputs || {};

  const ASSUMPTIONS = [
    ["Electricity Tariff",    inp.electricity_tariff_usd_kwh  != null ? `$${inp.electricity_tariff_usd_kwh}/kWh` : null,  "$/kWh — revenue per unit generated"],
    ["Tipping Fee",           inp.avoided_disposal_cost_usd_tonne != null ? `$${inp.avoided_disposal_cost_usd_tonne}/t` : null, "$/tonne — waste gate fee"],
    ["CAPEX",                 inp.capex_usd != null ? `$${Number(inp.capex_usd).toLocaleString()}` : null, "Total project capital cost"],
    ["Annual OPEX",           inp.opex_usd_yr != null ? `$${Number(inp.opex_usd_yr).toLocaleString()}/yr` : null, "Annual operating and maintenance costs"],
    ["Debt Ratio",            inp.debt_ratio_pct != null ? `${inp.debt_ratio_pct}%` : null, "% of CAPEX financed by debt"],
    ["Interest Rate",         inp.interest_rate_pct != null ? `${inp.interest_rate_pct}%` : null, "Annual cost of debt"],
    ["Loan Tenor",            inp.loan_tenor_years != null ? `${inp.loan_tenor_years} years` : null, "Debt repayment period"],
    ["Carbon Price (scenario)", "$10–$35/tCO₂e", "Illustrative range only — not contracted"],
    ["Carbon Revenue (base)",  fo.revenue_carbon_base_usd_yr > 0 ? `$${Number(fo.revenue_carbon_base_usd_yr).toLocaleString()}/yr` : null, "At $20/tCO₂e — illustrative, not contracted"],
    ["Contracted Revenue",    fo.contracted_revenue_pct != null ? `${fo.contracted_revenue_pct}%` : null, "% of revenue with signed agreements"],
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="financial-assumptions">
      <button className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 text-left"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2">
          <DollarSign size={13} className="text-blue-600" />
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Financial Assumptions</p>
          <span className="text-xs text-amber-600 font-semibold">· All figures are indicative only</span>
        </div>
        {open ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 py-4 space-y-2">
          {ASSUMPTIONS.map(([label, value, hint]) => (
            <div key={label} className="flex items-start justify-between gap-2 py-1.5 border-b border-slate-50 last:border-0">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-700">{label}</p>
                <p className="text-xs text-slate-400 italic">{hint}</p>
              </div>
              <span className={`text-xs font-bold flex-shrink-0 ${value ? "text-slate-900" : "text-slate-300"}`}>
                {value || "Assumption not defined — model default used"}
              </span>
            </div>
          ))}
          <div className="pt-2 mt-2 border-t border-slate-100">
            <p className="text-xs text-amber-600 italic">
              DSCR and IRR are computed from the above assumptions and are indicative only.
              All financial projections require independent due diligence before investment decisions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 5. Carbon Crediting Readiness Panel ───────────────────────────────────────
export function CarbonReadinessPanel({ projectState, journey }) {
  const [open, setOpen] = useState(false);
  const cp   = (projectState?.carbon_pathway) || {};
  const base = (journey?.climate_outputs)  || {};
  const ev   = (projectState?.evidence)    || {};
  const rep  = (projectState?.report)      || {};
  const inp  = journey?.input_snapshot    || {};
  const red  = journey?.reduction         || {};
  const verLevel = rep.verification_level || "preliminary";

  const baseConfirmed = base.baseline_confirmed || false;
  const evCovPct      = ev.coverage_pct || 0;
  const auditReady    = ev.audit_ready  || false;
  const vcmOk         = (cp.vcm || {}).eligible;
  const art6Ok        = (cp.art6 || {}).eligible;
  const methodologyBasis = inp.feedstock_methodology_basis || "Engineering Estimate";
  const digesterCfg  = inp.digester_configuration || "single_stage";
  const hasManureCorrection = (journey?.baseline?.feedstock_split_streams || [])
    .some(s => s.type === "manure_management");
  const hasGridEFStandard = Math.abs(parseFloat(inp.grid_factor_kgco2e_kwh || 0) - 0.575) < 0.005;
  const combinedBaselineOk = (red.combined_baseline_tco2e_yr || 0) > (red.scope1_baseline_tco2e_yr || 0);

  const STATUS = {
    NOT_STARTED:  { label: "Not started",             color: "text-slate-400", dot: "bg-slate-300" },
    IN_PROGRESS:  { label: "In progress",              color: "text-amber-600", dot: "bg-amber-400" },
    PARTIAL:      { label: "Partially addressed",     color: "text-amber-600", dot: "bg-amber-400" },
    DONE:         { label: "Addressed",                color: "text-teal-600",  dot: "bg-teal-500"  },
    PHASE2:       { label: "Phase 2 activity",         color: "text-blue-500",  dot: "bg-blue-400"  },
    NA:           { label: "Not applicable",           color: "text-slate-400", dot: "bg-slate-200" },
    CORRECTED:    { label: "Phase 1 Corrected",        color: "text-emerald-600",dot:"bg-emerald-500"},
  };

  const currentStatus = {
    label: "Methodology Classification",
    status: hasManureCorrection ? STATUS.CORRECTED : STATUS.PARTIAL,
    note: hasManureCorrection
      ? "✓ Manure streams correctly classified under IPCC Vol.4 Ch.10 (not agricultural residues)"
      : "Animal manure streams require reclassification from agricultural residues to manure_management",
  };

  const items = [
    currentStatus,
    {
      label: "Feedstock Characterization Basis",
      status: methodologyBasis === "Site Measured" ? STATUS.DONE : methodologyBasis.includes("Laboratory") ? STATUS.IN_PROGRESS : STATUS.PARTIAL,
      note:   `Current: ${methodologyBasis}. Phase 2: upgrade to Laboratory Characterized (TS/VS/BMP analysis).`,
    },
    {
      label: "TS/VS Laboratory Testing",
      status: STATUS.PHASE2,
      note:   "Phase 2 activity — site-specific Total Solids and Volatile Solids characterization pending. Required to replace T2 proxy DOC.",
    },
    {
      label: "Biomethane Potential (BMP) Testing",
      status: STATUS.PHASE2,
      note:   "Phase 2 — BMP laboratory analysis required to replace engineering proxy with site-specific methane potential.",
    },
    {
      label: "Grid EF Standardization",
      status: hasGridEFStandard ? STATUS.CORRECTED : STATUS.PARTIAL,
      note:   hasGridEFStandard
        ? "✓ WAPP CDM ASB0034-2021 (0.575 kgCO₂e/kWh) applied"
        : "Grid emission factor not yet standardized to WAPP CDM ASB0034-2021",
    },
    {
      label: "Combined Baseline Reconciliation",
      status: combinedBaselineOk ? STATUS.CORRECTED : STATUS.PARTIAL,
      note:   combinedBaselineOk
        ? `✓ Combined Baseline = Scope 1 (${(red.scope1_baseline_tco2e_yr||0).toFixed(1)}) + Scope 2 ref (${(red.scope2_reference_baseline_tco2e_yr||0).toFixed(1)}) = ${(red.combined_baseline_tco2e_yr||0).toFixed(1)} tCO₂e/yr`
        : "Combined baseline should include Scope 1 + Scope 2 reference baseline",
    },
    {
      label: "Monitoring Plan / MRV Framework",
      status: evCovPct >= 70 ? STATUS.IN_PROGRESS : STATUS.PARTIAL,
      note:   "Phase 2 — UNFCCC-compliant monitoring plan development required for VVB engagement.",
    },
    {
      label: "PDD Development",
      status: STATUS.PHASE2,
      note:   "Phase 2 — Project Design Document to be developed with accredited DOE/VVB partner.",
    },
    {
      label: "VVB Validation",
      status: verLevel === "verified_independent" ? STATUS.DONE : STATUS.NOT_STARTED,
      note:   "Phase 2 — Independent validation by UNFCCC-accredited VVB required for registry registration.",
    },
    {
      label: "Registry Registration",
      status: STATUS.NOT_STARTED,
      note:   "Phase 2 — VCS or Gold Standard registry registration after VVB validation.",
    },
    {
      label: "Article 6 / NDC Alignment",
      status: STATUS.NOT_STARTED,
      note:   "Phase 2 — Host country authorization (NCCC/FMEnv Nigeria) required for ITMO pathway.",
    },
  ];

  const correctedCount = items.filter(i => i.status === STATUS.CORRECTED).length;
  const phase2Count    = items.filter(i => i.status === STATUS.PHASE2).length;

  const overallStatus = correctedCount >= 3
    ? `Phase 1 complete — ${phase2Count} Phase 2 activities remaining`
    : items.some(i => i.status === STATUS.DONE || i.status === STATUS.IN_PROGRESS)
    ? "Developing — pre-feasibility stage"
    : "Pre-feasibility — not yet eligible for credit issuance";

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="carbon-readiness">
      <button className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 text-left"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2">
          <Leaf size={13} className="text-emerald-600" />
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Carbon Monetization Readiness</p>
          <span className="text-xs text-amber-600 font-semibold">· {correctedCount > 0 ? `${correctedCount} Phase 1 corrections verified` : overallStatus}</span>
        </div>
        {open ? <ChevronUp size={13} className="text-slate-400" /> : <ChevronDown size={13} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-5 py-4 space-y-3">
          {/* Phase 1 corrections banner */}
          {correctedCount > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-2">
              <p className="text-xs text-emerald-700 font-semibold">
                ✓ {correctedCount} Phase 1 technical corrections verified — methodology classification, combined baseline, grid EF standardization
              </p>
            </div>
          )}
          {items.map(item => (
            <div key={item.label} className="flex items-start gap-2.5">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${item.status.dot}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-slate-800">{item.label}</p>
                  <span className={`text-xs flex-shrink-0 ${item.status.color}`}>{item.status.label}</span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">{item.note}</p>
              </div>
            </div>
          ))}
          <div className="pt-3 mt-2 border-t border-slate-100 space-y-1.5">
            <p className="text-xs font-bold text-slate-700">Required Phase 2 Activities:</p>
            {[
              "1. TS/VS Laboratory Testing",
              "2. Biomethane Potential (BMP) Testing",
              "3. Feedstock Verification",
              "4. Monitoring Plan Development",
              "5. PDD Development (with accredited DOE/VVB)",
              "6. VVB Validation",
              "7. Registry Registration",
              "8. Article 6 Host Country Authorization (NCCC/FMEnv Nigeria)",
            ].map((step,i) => (
              <p key={i} className="text-xs text-slate-500 flex items-start gap-1.5">
                <span className="text-slate-300 flex-shrink-0">→</span>{step}
              </p>
            ))}
          </div>
          <div className="pt-2 mt-2 border-t border-slate-100">
            <p className="text-xs text-amber-600 italic font-semibold">
              Phase 1 complete — NOT currently eligible for carbon credit issuance without completing Phase 2 VVB validation and registry registration.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 6. Output Classification Tag ──────────────────────────────────────────────
export function OutputClassificationTag({ verificationLevel, type = "metric" }) {
  const tags = {
    preliminary:          "Calculated Estimate",
    uploaded:             "Calculated Estimate",
    reviewed_internal:    "Calculated (Internally Reviewed)",
    verified_independent: "Calculated Estimate",
    certified:            "Certified",
  };
  const colors = {
    preliminary:          "text-slate-400",
    uploaded:             "text-blue-400",
    reviewed_internal:    "text-teal-500",
    verified_independent: "text-emerald-600",
    certified:            "text-emerald-700 font-semibold",
  };
  const tag = tags[verificationLevel] || "Calculated Estimate";
  const cls = colors[verificationLevel] || "text-slate-400";
  return (
    <span className={`text-xs italic ${cls}`} data-testid="output-classification-tag">
      ({tag})
    </span>
  );
}

// ── Compact disclosure notice (for footers and inline use) ────────────────────
export function DisclosureNotice({ short = false }) {
  if (short) return (
    <p className="text-xs text-slate-400 italic">
      Model-based estimates · Not independently verified · Not a carbon credit
    </p>
  );
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 mt-4">
      <p className="text-xs text-slate-500 leading-relaxed">
        <strong className="text-slate-600">Disclosure:</strong> Results presented are model-based estimates generated
        by the DeCarbonIQ platform from user-provided inputs and defined assumptions.
        They have not undergone third-party verification or certification.
        DSCR and IRR are indicative only and require independent due diligence.
        This output does not constitute a carbon credit, financial instrument, or verified emission reduction.
      </p>
    </div>
  );
}
